import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, type UseFormReturn } from "react-hook-form";
import Select from "react-select";
import Modal from "../../../../../../components/base/Modal";
import type {
  Notification,
  NotificationDto,
} from "../../../../../../store/admin/notificationsStore";
import type { User } from "../../../../../../store/admin/usersStore";
import type { Role } from "../../../../../../store/admin/rolesStore";
import type { ThemeClasses } from "../../../../shared";
import type { StylesConfig } from "react-select";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import Tooltip from "../../../../../../components/ui/Tooltip";

type RecipientMode = "all" | "specific";

interface SelectedUser {
  id: string;
  firstName: string;
  lastName: string;
  username?: string;
}

interface EditNotificationModalProps {
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  customSelectStyles: StylesConfig;
  form: UseFormReturn<NotificationDto>;
  users: User[];
  roles: Role[];
  notification: Notification;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (
    data: NotificationDto,
    file: File | null,
    removeImage?: boolean,
    selectedUsers?: string[]
  ) => void;
}

const NOTIFICATION_ICONS = [
  {
    id: "warning-red",
    icon: "ri-error-warning-fill",
    color: "text-red-500",
    bgSelected: "bg-red-500/20",
    ringColor: "ring-red-500",
    borderColor: "border-red-500",
  },
  {
    id: "warning-yellow",
    icon: "ri-alert-fill",
    color: "text-yellow-500",
    bgSelected: "bg-yellow-500/20",
    ringColor: "ring-yellow-500",
    borderColor: "border-yellow-500",
  },
  {
    id: "chat-blue",
    icon: "ri-chat-3-fill",
    color: "text-blue-500",
    bgSelected: "bg-blue-500/20",
    ringColor: "ring-blue-500",
    borderColor: "border-blue-500",
  },
  {
    id: "success-green",
    icon: "ri-checkbox-circle-fill",
    color: "text-primary-500",
    bgSelected: "bg-primary-500/20",
    ringColor: "ring-primary-500",
    borderColor: "border-primary-500",
  },
];

export default function EditNotificationModal({
  isDarkMode,
  themeClasses,
  customSelectStyles,
  form,
  users,
  roles,
  notification,
  loadingSubmit,
  onClose,
  onSubmit,
}: EditNotificationModalProps) {
  const { t } = useTranslation("adminSections");
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
  } = form;
  const disableCRUD = useDisableCRUD();
  const [editImagePreview, setEditImagePreview] = useState<string | null>(
    notification.image || null
  );

  const [editSelectedFile, setEditSelectedFile] = useState<File | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("all");

  // Filter users by selected role
  const usersFilteredByRole = selectedRoleId === "all"
    ? users
    : users.filter((u) => u.role_id === selectedRoleId);

  // Role options for selector
  const roleOptions = [
    { value: "all", label: t("notifications.form.allRoles") },
    ...roles.map((role) => ({ value: role.id, label: role.name })),
  ];

  const [recipientMode, setRecipientMode] = useState<RecipientMode>(() => {
    // Check if all users are selected (type_of_addresse === "all" or addresses contains all users)
    if (notification.type_of_addresse === "all") return "all";
    if (notification.addresses && notification.addresses.length === users.length) return "all";
    return "specific";
  });
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>(() => {
    const result: SelectedUser[] = [];
    // Initialize from addresses array
    if (notification.addresses && notification.addresses.length > 0) {
      notification.addresses.forEach((userId) => {
        const user = users.find((u) => u.id === userId);
        if (user) {
          result.push({
            id: user.id,
            firstName: user.first_name,
            lastName: user.last_name,
            username: user.username,
          });
        }
      });
      return result;
    }
    // Fallback to legacy addressee field
    if (notification.addressee && notification.addressee !== "all") {
      const user = users.find((u) => u.id === notification.addressee);
      if (user) {
        return [{
          id: user.id,
          firstName: user.first_name,
          lastName: user.last_name,
          username: user.username,
        }];
      }
    }
    return [];
  });

  const watchedIcon = watch("icon");

  const priorityOptions = [
    { value: "low", label: t("notifications.priority.low") },
    { value: "medium", label: t("notifications.priority.medium") },
    { value: "high", label: t("notifications.priority.high") },
    { value: "urgent", label: t("notifications.priority.urgent") },
  ];

  const handleRoleChange = (roleId: string) => {
    setSelectedRoleId(roleId);
    setSelectedUsers([]);
    setValue("addressee", "");
  };

  const handleEditImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setEditSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearEditImage = () => {
    setImageRemoved(true);
    setEditImagePreview(null);
    setEditSelectedFile(null);
    const input = document.getElementById("image-edit") as HTMLInputElement;
    if (input) input.value = "";
  };

  const handleFormSubmit = (data: NotificationDto) => {
    if (recipientMode === "all") {
      const allUserIds = usersFilteredByRole.map((user) => user.id);
      onSubmit(data, editSelectedFile, imageRemoved, allUserIds);
    } else {
      const userIds = selectedUsers.map((user) => user.id);
      onSubmit(data, editSelectedFile, imageRemoved, userIds);
    }
  };

  const handleSelectAllUsers = () => {
    setRecipientMode("all");
    setSelectedUsers([]);
    setValue("addressee", "all");
  };

  const handleSelectSpecificUsers = () => {
    setRecipientMode("specific");
    setValue("addressee", "");
  };

  const handleAddUser = (userId: string) => {
    const user = usersFilteredByRole.find((u) => u.id === userId);
    if (user && !selectedUsers.some((su) => su.id === userId)) {
      const newUser: SelectedUser = {
        id: user.id,
        firstName: user.first_name,
        lastName: user.last_name,
        username: user.username,
      };
      setSelectedUsers([...selectedUsers, newUser]);
      setValue("addressee", user.id);
    }
  };

  const handleRemoveUser = (userId: string) => {
    const newList = selectedUsers.filter((u) => u.id !== userId);
    setSelectedUsers(newList);
    setValue("addressee", newList.length > 0 ? newList[0].id : "");
  };

  const availableUsers = usersFilteredByRole.filter(
    (user) => !selectedUsers.some((su) => su.id === user.id)
  );

  const userOptions = availableUsers.map((user) => ({
    value: user.id,
    label: `${user.first_name} ${user.last_name}`,
  }));

  return (
    <Modal
      themeClasses={themeClasses}
      onClose={onClose}
      title={t("notifications.editNotification")}
      content={
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-4 w-[500px] max-w-full"
        >
          {/* Icon Selector */}
          <div className="flex flex-col gap-2 w-full">
            <label className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("notifications.fields.icon")}
            </label>
            <div
              className={`flex justify-center gap-4 p-3 rounded-lg border w-full ${themeClasses.cardBorder
                } ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
            >
              {NOTIFICATION_ICONS.map((item) => {
                const isSelected = watchedIcon === item.id;
                return (
                  <Tooltip
                    key={item.id}
                    content={t(`notifications.icons.${item.id}`)}
                    position="top"
                    className="!w-fit"
                  >
                    <button
                      type="button"
                      onClick={() => setValue("icon", item.id)}
                      className={`flex-1 max-w-[40px] aspect-square rounded-lg flex items-center justify-center transition-all duration-200 p-2 ${isSelected
                        ? `${item.bgSelected} ring-2 ${item.ringColor} scale-110 ${item.borderColor}`
                        : isDarkMode
                          ? "bg-dark-surface hover:bg-dark-border"
                          : "bg-white hover:bg-gray-100"
                        } border ${isSelected ? item.borderColor : themeClasses.cardBorder
                        }`}
                    >
                      <i className={`${item.icon} text-xl ${item.color}`}></i>
                    </button>
                  </Tooltip>
                );
              })}
            </div>
            {errors.icon && (
              <p className="text-red-600 text-sm">{errors.icon.message}</p>
            )}
          </div>

          {/* Title */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="title-edit"
              className={`text-sm font-semibold ${themeClasses.text}`}
            >
              {t("notifications.fields.title")}
            </label>
            <input
              type="text"
              id="title-edit"
              {...register("title", {
                required: t("notifications.form.requiredField"),
              })}
              placeholder={t("notifications.form.titlePlaceholder")}
              className={`w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm ${errors.title ? "border-red-500" : ""
                }`}
            />
            {errors.title && (
              <p className="text-red-600 text-sm">{errors.title.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="description-edit"
              className={`text-sm font-semibold ${themeClasses.text}`}
            >
              {t("notifications.fields.description")}
            </label>
            <textarea
              id="description-edit"
              rows={3}
              {...register("description", {
                required: t("notifications.form.requiredField"),
              })}
              placeholder={t("notifications.form.descriptionPlaceholder")}
              className={`w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm resize-none ${errors.description ? "border-red-500" : ""
                }`}
            />
            {errors.description && (
              <p className="text-red-600 text-sm">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Image */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="image-edit"
              className={`text-sm font-semibold ${themeClasses.text} flex items-center gap-1`}
            >
              {t("notifications.fields.attachImage")}
              <i className="ri-attachment-2 text-gray-400"></i>
            </label>
            <div className="relative">
              <input
                type="file"
                id="image-edit"
                accept="image/*"
                className="hidden"
                onChange={handleEditImageChange}
              />
              {!editImagePreview ? (
                <div
                  onClick={() => document.getElementById("image-edit")?.click()}
                  className={`w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                    } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                    } text-sm cursor-pointer flex items-center justify-between hover:border-primary-500 transition-colors`}
                >
                  <span className="text-gray-400">
                    {t("notifications.form.noFileAttached")}
                  </span>
                  <i className="ri-image-add-line text-gray-400"></i>
                </div>
              ) : (
                <div
                  className={`relative p-3 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                    } border ${themeClasses.cardBorder} rounded-lg`}
                >
                  <div className="relative inline-block">
                    <img
                      src={editImagePreview}
                      alt="Preview"
                      className="max-h-32 rounded-md object-cover"
                    />
                    <button
                      aria-label={t("general.close", { ns: "common" })}
                      type="button"
                      onClick={clearEditImage}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                  {editSelectedFile && (
                    <p
                      className={`text-xs ${themeClasses.textSecondary} mt-2 truncate`}
                    >
                      {editSelectedFile.name}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Priority */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="priority-edit"
              className={`text-sm font-semibold ${themeClasses.text}`}
            >
              {t("notifications.fields.priority")}
            </label>
            <Controller
              name="priority"
              control={control}
              rules={{ required: t("notifications.form.requiredField") }}
              render={({ field }) => (
                <Select
                  {...field}
                  inputId="priority-edit"
                  value={priorityOptions.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(option: any) => setValue("priority", option.value)}
                  options={priorityOptions}
                  styles={customSelectStyles}
                  placeholder={t("notifications.form.selectPriority")}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              )}
            />
            {errors.priority && (
              <p className="text-red-600 text-sm">{errors.priority.message}</p>
            )}
          </div>

          {/* Schedule */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="schedule_shipment_edit"
              className={`text-sm font-semibold ${themeClasses.text}`}
            >
              {t("notifications.fields.scheduleShipment")}
            </label>
            <input
              type="datetime-local"
              id="schedule_shipment_edit"
              {...register("schedule_shipment")}
              className={`w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm`}
            />
          </div>

          {/* Recipients Section */}
          <div className="flex flex-col gap-3">
            <label className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("notifications.fields.addressee")}
            </label>

            {/* Role Selector */}
            <div className="flex flex-col gap-1">
              <label className={`text-xs ${themeClasses.textSecondary}`}>
                {t("notifications.form.filterByRole")}
              </label>
              <Select
                value={roleOptions.find((opt) => opt.value === selectedRoleId)}
                onChange={(option: any) => handleRoleChange(option?.value || "all")}
                options={roleOptions}
                styles={customSelectStyles}
                placeholder={t("notifications.form.selectRole")}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                closeMenuOnSelect={false}
              />
            </div>

            {/* Recipient Mode Toggle */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSelectSpecificUsers}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${recipientMode === "specific"
                  ? "bg-primary-500 text-white border-primary-500"
                  : isDarkMode
                    ? "bg-dark-card border-dark-border text-gray-300 hover:bg-dark-border"
                    : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <i className="ri-user-line mr-2"></i>
                {t("notifications.form.specificUsers")}
              </button>
              <button
                type="button"
                onClick={handleSelectAllUsers}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${recipientMode === "all"
                  ? "bg-primary-500 text-white border-primary-500"
                  : isDarkMode
                    ? "bg-dark-card border-dark-border text-gray-300 hover:bg-dark-border"
                    : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <i className="ri-group-line mr-2"></i>
                {t("notifications.form.allUsers")}
                <span className="ml-1 text-xs opacity-75">({usersFilteredByRole.length})</span>
              </button>
            </div>

            {/* User Search Select (only shown when specific mode) */}
            {recipientMode === "specific" && (
              <>
                <Controller
                  name="addressee"
                  control={control}
                  rules={{
                    validate: () =>
                      selectedUsers.length > 0 ||
                      (t("notifications.form.requiredField") as string),
                  }}
                  render={() => (
                    <Select
                      inputId="addressee-edit"
                      value={null}
                      onChange={(option: any) => {
                        if (option) {
                          handleAddUser(option.value);
                        }
                      }}
                      options={userOptions}
                      styles={customSelectStyles}
                      placeholder={t("notifications.form.selectAddressee")}
                      isSearchable={true}
                      noOptionsMessage={() => t("notifications.form.noUsersFound")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      closeMenuOnSelect={false}
                    />
                  )}
                />

                {/* Selected Users List */}
                {selectedUsers.length > 0 && (
                  <div
                    className={`rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"
                      } max-h-32 overflow-y-auto`}
                  >
                    <div className="p-2 space-y-1">
                      {selectedUsers.map((user) => (
                        <div
                          key={user.id}
                          className={`flex items-center justify-between p-2 rounded-md ${isDarkMode ? "bg-dark-card" : "bg-white"
                            } border ${themeClasses.cardBorder}`}
                        >
                          <div className="flex flex-col">
                            <span className={`text-sm font-medium ${themeClasses.text}`}>
                              {user.firstName} {user.lastName}
                            </span>
                            {user.username && (
                              <span className={`text-xs ${themeClasses.textSecondary}`}>
                                @{user.username}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveUser(user.id)}
                            className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-500 transition-colors"
                            title={t("notifications.form.removeUser")}
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedUsers.length > 0 && (
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    {selectedUsers.length} {t("notifications.form.usersSelected")}
                  </p>
                )}

                {errors.addressee && selectedUsers.length === 0 && (
                  <p className="text-red-600 text-sm">{errors.addressee.message}</p>
                )}
              </>
            )}

            {/* All Users Info (shown when all mode) */}
            {recipientMode === "all" && (
              <div
                className={`flex items-center gap-3 p-3 rounded-lg border ${isDarkMode
                  ? "bg-dark-card border-dark-border"
                  : "bg-primary-50 border-primary-200"
                  }`}
              >
                <i className={`ri-checkbox-circle-fill text-xl ${isDarkMode ? "text-primary-500" : "text-primary-500"
                  }`}></i>
                <div>
                  <p className={`text-sm font-medium ${themeClasses.text}`}>
                    {t("notifications.form.allUsersSelected")}
                  </p>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>
                    {t("notifications.form.willNotify")} {users.length} {t("notifications.form.usersOfCompany")}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg border text-center ${isDarkMode
                ? "bg-dark-card border-dark-border text-white hover:bg-dark-border"
                : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                } transition-colors font-medium`}
            >
              {t("notifications.form.cancel")}
            </button>
            <CRUDTooltip>
              <button
                type="submit"
                disabled={loadingSubmit || disableCRUD}
                className="bg-primary-500 hover:bg-primary-400 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {loadingSubmit
                  ? t("notifications.form.submitting")
                  : t("notifications.form.saveChanges")}
              </button>
            </CRUDTooltip>
          </div>
        </form>
      }
    />
  );
}
